import {
  StringSignalCondition,
  StringSignalEffect,
  StringSignalOperator,
  StringSignalRule,
} from "../models/signal/StringSignalRule";

const CONDITION_PATTERN =
  /^([a-zA-Z0-9_-]+)\.([a-zA-Z0-9_-]+)\s*(>=|<=|==|!=|>|<)\s*(-?(?:\d+\.?\d*|\d*\.?\d+))$/;

const RULE_PATTERN = /^(.*?)\s*=>\s*([a-z.]+)\((.*)\)$/;

export function parseSignalRules(value: string): StringSignalRule[] {
  return value
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .flatMap((raw) => {
      const match = raw.match(RULE_PATTERN);
      if (!match) return [];
      const [, expressionRaw, effectType, effectArgsRaw] = match;
      const groups = parseExpression(expressionRaw.trim());
      if (!groups.length) return [];
      const effect = parseEffect(effectType.trim(), effectArgsRaw.trim());
      if (!effect) return [];
      return [{ groups, effect, raw }];
    });
}

function parseExpression(value: string): StringSignalCondition[][] {
  return value
    .split(/\s*\|\|\s*/)
    .filter(Boolean)
    .map((orPart) =>
      orPart
        .split(/\s*&&\s*/)
        .filter(Boolean)
        .map(parseCondition),
    )
    .filter((group): group is StringSignalCondition[] =>
      group.length > 0 && group.every(Boolean),
    );
}

function parseCondition(raw: string): StringSignalCondition | null {
  const match = raw.match(CONDITION_PATTERN);
  if (!match) return null;

  const [, sourceId, signal, operator, thresholdRaw] = match;
  const threshold = parseFloat(thresholdRaw);
  if (!Number.isFinite(threshold)) return null;

  return {
    sourceId,
    signal,
    operator: operator as StringSignalOperator,
    threshold,
    raw,
  };
}

function parseEffect(effectType: string, argsRaw: string): StringSignalEffect | null {
  const args = splitArgs(argsRaw);

  switch (effectType) {
    case "toggle.class":
      return args[0] ? { type: "class-toggle", key: args[0] } : null;
    case "class.add":
      return args[0] ? { type: "class-add", key: args[0] } : null;
    case "class.remove":
      return args[0] ? { type: "class-remove", key: args[0] } : null;
    case "style.set":
      return args[0] && args[1] ? { type: "style-set", key: args[0], value: args[1] } : null;
    case "style.remove":
      return args[0] ? { type: "style-remove", key: args[0] } : null;
    case "var.set":
      return args[0] && args[1] ? { type: "var-set", key: args[0], value: args[1] } : null;
    case "var.remove":
      return args[0] ? { type: "var-remove", key: args[0] } : null;
    default:
      return null;
  }
}

function splitArgs(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
